import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppContext from "../../utils/AppContext";
import useGlobalParams from "../../hooks/useGlobalParams";

import useMutateBusiness from "../../hooks/useMutateBusiness";
import useBusinesses from "../../hooks/useBusinesses";
import useToast from "../../hooks/useToast";
import { handleNetworkError } from "../../utils/api";
import { isAdminUser } from "../../utils/userUtils";

import PLink from "../../common/Navigation/PLink";
import CategoriesList from "./CategoriesList";
import ConfirmationModal from "../../common/ConfirmationModal/ConfirmationModal";
import OTPModal from "../../common/OTPModal/OTPModal";
import Form from 'react-bootstrap/Form';
import Button from "../../common/Button/Button";
import { Trans as T } from 'react-i18next';
import Toast from "../../common/Toast/Toast";

import './BusinessSettings.scss';

export default function BusinessSettings(props) {
  const [state, dispatch] = useContext(AppContext);
  const navigate = useNavigate();
  const { toast, showToast } = useToast();
  const { organizationId, businessId } = useGlobalParams();
  
  const [businessName, setBusinessName] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [nameErrors, setNameErrors] = useState(null);
  const [nameValidated, setNameValidated] = useState(false);
  const [namePending, setNamePending] = useState(false);
  const [showOTP, setShowOTP] = useState(false);  
  
  const { data: businesses } = useBusinesses({ organization_id: organizationId });  
  const { updateBusiness, deleteBusiness } = useMutateBusiness({ id: businessId });
  
  const { currentUserRole } = state;
  const business = businesses?.find(business => business.id === businessId);

  // Only Admins can see the business settings page:
  useEffect(() => {
    if (currentUserRole && !isAdminUser(currentUserRole)) {
      navigate('/');
    }
  }, [currentUserRole, navigate]);

  useEffect(() => {
    if (business && businessName === null) {
      setBusinessName(business.name);
    }
  }, [business, businessName]);

  const handleRename = e => {
    e.preventDefault();

    setNameErrors(null);

    if (businessName.trim() === business.name) {
      setNameValidated(false);
      return;
    }

    setNamePending(true);
    setNameValidated(false);
    setBusinessName(businessName.trim())

    const onSuccess = () => {
      showToast('Changes saved');
      document.activeElement.blur();
    }

    const onError = e => {
      handleNetworkError({ e, setErrors: setNameErrors, setValidated: setNameValidated });
    }

    const onSettled = () => {
      setNamePending(false);
    }

    updateBusiness.mutate({ data: { name: businessName.trim() }, id: businessId }, { onSuccess, onError, onSettled });
  }

  const changeLanguage = e => {
    const onSuccess = () => {
      showToast('Changes saved');
    }

    const language = e.target.value;
    updateBusiness.mutate({ data: { language }, id: businessId }, { onSuccess });
  }

  const onDeleteBusiness = e => {
    setShowDelete(true);
  }

  const checkForVerification = e => {
    const code = e?.response?.data?.code;

    if (code === 'verification_required') {
      setShowOTP(true);
    }

    else {
      console.log(e);
    }
  }

  const onConfirmDelete = async () => {
    setShowDelete(false);
    
    const onError = checkForVerification;
    
    const onSuccess = () => {
      dispatch({ type: 'globalToast/set', payload: { message: "Business deleted from organization" }});
      navigate('/');
    }

    deleteBusiness.mutate(businessId, { onSuccess, onError });
  }

  if (!currentUserRole || !businesses) {
    return;
  }

  const renderContent = () => (
    <div className="content">

      <h2><T>Business Settings</T></h2>

      <div className="section">
        <Form onSubmit={handleRename} className="single-field">
          <Form.Label><T>Business name</T></Form.Label>
          <Form.Group className="input-button">
            <Form.Control value={businessName || ''} onChange={e => setBusinessName(e.target.value)} isValid={nameValidated && !nameErrors?.name} isInvalid={nameErrors?.name} />
            <Button type='submit' loading={namePending} disabled={businessName?.trim().length === 0}><T>Rename</T></Button>
            { nameErrors?.name?.map(error => <Form.Control.Feedback key={error} type="invalid">{ error }</Form.Control.Feedback> )}
            { nameErrors?.non_field_errors?.map(error => <div className="errors" key={error}>{ error }</div> )}
            </Form.Group>
        </Form>
      </div>

      <div className="section">
        <Form.Group className='single-field'>
          <Form.Label><T>Language</T></Form.Label>
          <Form.Select value={business.language} onChange={changeLanguage}>
            <option value="en">English</option>
            <option value="es">Español</option>
          </Form.Select>
        </Form.Group>
      </div>

      <CategoriesList showToast={showToast} />

      <h2><T>Users & Roles</T></h2>
      
      <div className="section">
        <p className="user-roles">To manage users and roles, go to your <PLink to='/users'>User & Roles.</PLink></p>
      </div>

      <section className='danger-zone'>
        <h2>Delete Business</h2>

        <div className='delete-business'>
          <div className='warning-message'>If you delete this business, all leads and categories will be inaccessible to all users. Make sure you’re certain.</div>
          <Button className='delete' onClick={onDeleteBusiness}>Delete Business</Button>
        </div>
      </section>

      <Toast toast={toast} hideClose />

      <ConfirmationModal title='Delete Business' inputConfirm show={showDelete} onHide={() => setShowDelete(false)} onConfirm={onConfirmDelete} confirmText='Delete'>
        <p>Are you sure you want to delete this business?</p>
        <p>You will not be able to access any categories or leads associated to this business</p>
      </ConfirmationModal>

      <OTPModal show={showOTP} onHide={() => setShowOTP(false)} onConfirm={onConfirmDelete} />
    </div>
  );

  return (
    <section className='__business-settings'>
      { renderContent() }
    </section>
  )
}
